import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { StringUtils } from '@ark-libs/ngx-utils';

export class DataSource<T> {
    private DATA_UUID = "__cached_data_uuid";
    private NODE_UUID = "__cached_node_uuid";

    private _cachedDatas: { url:string, params?:{[key:string]:string|number}, promise?:Promise<T> }[] = [];
    ignoreHttpError:boolean = true;
    enableCache:boolean;

    private _http: HttpClient;
    private _url: string;

    constructor(http: HttpClient, url: string, enableCache?:boolean) {
        this._http = http;
        this._url = url;
        this.enableCache = enableCache;
    }

    post(data: any, params?: { [param: string]: any | any[] }): Observable<HttpResponse<any>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params);
        return this._http.post<any>(url, data, { observe: 'response', params: this.formatParams(params) });
    }
    put(data: any, params?: { [param: string]: any | any[] }): Observable<HttpResponse<any>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params);
        return this._http.put<any>(url, data, { observe: 'response', params: this.formatParams(params) });
    }

    query(params?: { [param: string]: any | any[] }, forceCache?:boolean): Observable<HttpResponse<any>> {
        const _this = this;
        const url = this.transInPathParams(this._url, params);
        return this.tryGetFromCache(url, params, function(){
            return _this._http.get<any>(url, { observe: 'response', params: _this.formatParams(params) });
        }, forceCache);
    }
    findList(params?: { [param: string]: any | any[] }, forceCache?:boolean): Observable<HttpResponse<T[]>> {
        const _this = this;
        const url = this.transInPathParams(this._url, params);
        return this.tryGetFromCache(url, params, function(){
            return _this._http.get<T[]>(url, { observe: 'response', params: _this.formatParams(params) });
        }, forceCache);
    }
    findOne(id: string | number | T | any, params?: { [param: string]: any | any[] }, forceCache?:boolean): Observable<HttpResponse<T>> {
        const _this = this;
        const url = this.transInPathParams(this._url, params, id);
        return this.tryGetFromCache(url, params, function(){
            return _this._http.get<T>(url, { observe: 'response', params: _this.formatParams(params) });
        }, forceCache);
    }
    delete(id: string | number | T | any, params?: { [param: string]: any | any[] }): Observable<HttpResponse<any>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params, id);
        return this._http.delete(url, { observe: 'response', params: this.formatParams(params) });
    }
    create(entity: T, params?: { [param: string]: any | any[] }): Observable<HttpResponse<T>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params);
        return this._http.post<T>(url, entity, { observe: 'response', params: this.formatParams(params) });
    }
    update(entity: T, params?: { [param: string]: any | any[] }): Observable<HttpResponse<T>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params);
        return this._http.put<T>(url, entity, { observe: 'response', params: this.formatParams(params) });
    }
    updateOne(id: string | number | T | any, entity: T, params?: { [param: string]: any | any[] }): Observable<HttpResponse<T>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params, id);
        return this._http.put<T>(url, entity, { observe: 'response', params: this.formatParams(params) });
    }
    createOne(id: string | number | T | any, entity?: T, params?: { [param: string]: any | any[] }): Observable<HttpResponse<T>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params, id);
        return this._http.post<T>(url, entity, { observe: 'response', params: this.formatParams(params) });
    }
    deleteOne(id: string | number | T | any, params?: { [param: string]: any | any[] }): Observable<HttpResponse<any>> {
        this.clearCache();
        const url = this.transInPathParams(this._url, params, id);
        return this._http.delete(url, { observe: 'response', params: this.formatParams(params) });
    }

    private parseId(id: string | number | T | any): string {
        if (id instanceof Object && (<Object>id)['id']) { id = (<Object>id)['id']; }

        if (id == null) { return ''; }
        if (typeof id === 'string') { return id; }
        return id.toString();
    }

    private transInPathParams(url: string, params: any, id?: string | number | T | any){
        if(id != null){
            id = this.parseId(id);
            if (url.indexOf('{id}') != -1) url = url.replace('{id}', id);
            else url = `${url}/${id}`;
        }

        if(params == null) return url;
        Object.keys(params).forEach(key => {
            if(Array.isArray(params[key])) return;
            if (url.indexOf('{'+key+'}') == -1) return;
            url = url.replace('{'+key+'}', params[key]);
            delete params[key];
        });
        return url;
    }

    private mergeParams<U>(params1: U, params2: U): U {
        if (params1 && params2) {
            return Object.assign({}, params1, params2);
        } else if (params1) {
            return params1;
        } else {
            return params2;
        }
    }

    private formatParams(params?: { [param: string]: any | any[] }) {
        if (params != undefined) {
            // if (params.keywords && this.hasSpecial(params.keywords)) {
            //     params.keywords = encodeURIComponent(params.keywords);
            // };
            for (const key of Object.keys(params)) {
                if (params[key] == null) {
                    delete params[key];
                } else if(Array.isArray(params[key])) {
                    params[key] = params[key].map(item=>item?item.toString():"");
                }else {
                    params[key] = params[key].toString();
                }
            }
        }
        return params;
    }

    private hasSpecial(str) {
        for (let i in str) {
            let asc = str.charCodeAt(i);
            if (!(asc >= 48 && asc <= 57 || asc >= 65 && asc <= 90 || asc >= 97 && asc <= 122)) {
                return true;
            }
        }
        return false;
    }


    private clearCache(){
        this._cachedDatas = [];
    }

    private tryGetFromCache(url:string, requestParams: {[key:string]:string|number}, httpProcess:Function, forceCache?:boolean){
        if(this.enableCache||forceCache){
            if(!requestParams) requestParams = {};
            let cachedDataItem = this._cachedDatas.filter(item=>{
                return StringUtils.isEqualIgnoreCase(item.url, url)?
                    this.filterMatch(item.params, requestParams): false;
            }).pop();
            if(cachedDataItem==null){
                cachedDataItem = { url:url, params: requestParams };
                this._cachedDatas.push(cachedDataItem);
            }
            let promise = cachedDataItem.promise;
            if(!promise){
                promise = httpProcess().toPromise();
                cachedDataItem.promise = promise;
            }
            return from(promise);
        }else{
            return httpProcess();
        }
    }
    private filterMatch(cachedParams:{}, requestParams:{}): boolean{
        return Object.keys(cachedParams).every(key=>cachedParams[key] == requestParams[key])?
               Object.keys(requestParams).every(key=>cachedParams[key] == requestParams[key]):false;
    }
}
